import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, Eye, Users, Calendar, DollarSign, Mail, 
  Phone, MapPin, Tag, ChevronDown, ChevronUp, Send, UserCheck, UserX, SplitSquareHorizontal, Bell, Zap, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingsCRMTab({ bookings, token, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Combine bookings into customer view
  useEffect(() => {
    buildCustomerList();
  }, [bookings]);

  const buildCustomerList = () => {
    // Group bookings by email to create customer profiles
    const customerMap = new Map();
    
    bookings.forEach(booking => {
      const email = booking.guest_email?.toLowerCase();
      if (!email) return;
      
      if (!customerMap.has(email)) {
        customerMap.set(email, {
          email: booking.guest_email,
          name: booking.guest_name || 'Unknown',
          phone: booking.guest_phone || '',
          bookings: [],
          totalSpent: 0,
          totalOwed: 0,
          firstBooking: booking.created_at,
          lastBooking: booking.created_at,
          hasAccount: !!booking.user_id
        });
      }
      
      const customer = customerMap.get(email);
      customer.bookings.push(booking);
      customer.totalSpent += booking.amount_paid || 0;
      customer.totalOwed += booking.remaining_amount || 0;
      customer.hasAccount = customer.hasAccount || !!booking.user_id;
      
      if (booking.created_at > customer.lastBooking) {
        customer.lastBooking = booking.created_at;
      }
      if (booking.created_at < customer.firstBooking) {
        customer.firstBooking = booking.created_at;
      }
    });
    
    // Convert to array and sort by last booking date
    const customerList = Array.from(customerMap.values())
      .sort((a, b) => new Date(b.lastBooking) - new Date(a.lastBooking));
    
    setCustomers(customerList);
    setLoading(false);
  };

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm);
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'with_balance') return matchesSearch && c.totalOwed > 0;
    if (activeTab === 'paid') return matchesSearch && c.totalOwed === 0;
    if (activeTab === 'guests') return matchesSearch && !c.hasAccount;
    
    return matchesSearch;
  });

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
      pending_info: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-800'}>
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const sendEmail = async (email, bookingId) => {
    try {
      await axios.post(`${API}/admin/resend-confirmation`, 
        { booking_id: bookingId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Confirmation email sent to ${email}`);
    } catch (err) {
      toast.error('Failed to send email');
    }
  };

  const sendSplitPaymentReminder = async (bookingId, travelerNum, email) => {
    try {
      await axios.post(`${API}/admin/send-split-reminder`, 
        { booking_id: bookingId, traveler_num: travelerNum },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Payment reminder sent to ${email}`);
    } catch (err) {
      toast.error('Failed to send reminder');
    }
  };

  const stats = {
    totalCustomers: customers.length,
    withBalance: customers.filter(c => c.totalOwed > 0).length,
    paidInFull: customers.filter(c => c.totalOwed === 0 && c.totalSpent > 0).length,
    guestBookings: customers.filter(c => !c.hasAccount).length,
    totalRevenue: customers.reduce((sum, c) => sum + c.totalSpent, 0),
    totalOutstanding: customers.reduce((sum, c) => sum + c.totalOwed, 0)
  };

  return (
    <div className="space-y-4" data-testid="bookings-crm-tab">
      {/* CRM Automation Buttons */}
      <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-lg border">
        <span className="text-sm font-medium text-muted-foreground mr-2 self-center">CRM Automation:</span>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/crm/auto-segment`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(`Auto-segmentation complete! Updated ${res.data.customers_updated} customers`);
              onRefresh();
            } catch (err) {
              toast.error('Failed to run auto-segmentation');
            }
          }}
          className="gap-2"
        >
          <Tag className="h-4 w-4" />
          Auto-Segment
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/crm/auto-tasks`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(`Created ${res.data.tasks_created} automated tasks`);
            } catch (err) {
              toast.error('Failed to create auto-tasks');
            }
          }}
          className="gap-2"
        >
          <Zap className="h-4 w-4" />
          Generate Tasks
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onRefresh}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="p-3">
          <div className="text-2xl font-bold text-primary">{stats.totalCustomers}</div>
          <div className="text-xs text-muted-foreground">Total Customers</div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-bold text-yellow-600">{stats.withBalance}</div>
          <div className="text-xs text-muted-foreground">With Balance</div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-bold text-green-600">{stats.paidInFull}</div>
          <div className="text-xs text-muted-foreground">Paid in Full</div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-bold text-blue-600">{stats.guestBookings}</div>
          <div className="text-xs text-muted-foreground">Guest Bookings</div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-bold text-green-600">${stats.totalRevenue.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">Total Revenue</div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-bold text-orange-600">${stats.totalOutstanding.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">Outstanding</div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Bookings
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">All ({stats.totalCustomers})</TabsTrigger>
              <TabsTrigger value="with_balance">Balance Due ({stats.withBalance})</TabsTrigger>
              <TabsTrigger value="paid">Paid ({stats.paidInFull})</TabsTrigger>
              <TabsTrigger value="guests">Guests ({stats.guestBookings})</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No customers found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCustomers.map((customer, idx) => (
                <div key={customer.email} className="border rounded-lg overflow-hidden">
                  {/* Customer Header */}
                  <button
                    onClick={() => setExpandedCustomer(expandedCustomer === idx ? null : idx)}
                    className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                        <span className="text-secondary font-semibold">
                          {customer.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="text-left">
                        <div className="font-medium flex items-center gap-2">
                          {customer.name}
                          {!customer.hasAccount && (
                            <Badge variant="outline" className="text-xs">Guest</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-3">
                          <span>{customer.email}</span>
                          {customer.phone && <span>• {customer.phone}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden sm:block">
                        <div className="text-sm font-medium">
                          {customer.bookings.length} booking{customer.bookings.length !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ${customer.totalSpent.toLocaleString()} paid
                        </div>
                      </div>
                      
                      {customer.totalOwed > 0 && (
                        <Badge className="bg-orange-100 text-orange-800">
                          ${customer.totalOwed.toLocaleString()} due
                        </Badge>
                      )}
                      
                      {expandedCustomer === idx ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Bookings */}
                  {expandedCustomer === idx && (
                    <div className="border-t bg-muted/20 p-4">
                      <div className="space-y-3">
                        {customer.bookings.map(booking => (
                          <div 
                            key={booking.id} 
                            className="bg-white rounded-lg p-3 border"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div>
                                <div className="font-medium text-sm">{booking.trip_title}</div>
                                <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {booking.tour_date || 'Date TBD'}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {booking.travelers} traveler{booking.travelers !== 1 ? 's' : ''}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3" />
                                    ${booking.total_amount?.toLocaleString() || 0}
                                  </span>
                                  {booking.split_payment && (
                                    <span className="flex items-center gap-1 text-blue-600">
                                      <SplitSquareHorizontal className="h-3 w-3" />
                                      Split Payment
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {getStatusBadge(booking.status)}
                                
                                {booking.remaining_amount > 0 && (
                                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                                    ${booking.remaining_amount.toLocaleString()} due
                                  </Badge>
                                )}
                                
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => sendEmail(customer.email, booking.id)}
                                  title="Resend confirmation email"
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Split Payment Tracking */}
                            {booking.split_payment && booking.payment_shares && booking.payment_shares.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                  <SplitSquareHorizontal className="h-3 w-3" />
                                  Payment Status by Traveler (${booking.price_per_person?.toLocaleString() || '?'}/person)
                                </div>
                                <div className="grid gap-2">
                                  {booking.payment_shares.map((share, shareIdx) => (
                                    <div 
                                      key={shareIdx}
                                      className={`flex items-center justify-between p-2 rounded-md text-sm ${
                                        share.status === 'paid' ? 'bg-green-50' : 'bg-yellow-50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {share.status === 'paid' ? (
                                          <UserCheck className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <UserX className="h-4 w-4 text-yellow-600" />
                                        )}
                                        <div>
                                          <span className="font-medium">Traveler {share.traveler_num}</span>
                                          <span className="text-muted-foreground ml-2">{share.email}</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge className={share.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                          {share.status === 'paid' ? 'Paid' : 'Pending'}
                                        </Badge>
                                        {share.status !== 'paid' && share.traveler_num > 1 && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => sendSplitPaymentReminder(booking.id, share.traveler_num, share.email)}
                                            title="Send payment reminder"
                                            className="text-yellow-600 hover:text-yellow-700"
                                          >
                                            <Bell className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {booking.payment_shares.some(s => s.status !== 'paid') && (
                                  <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 p-2 rounded">
                                    💡 {booking.payment_shares.filter(s => s.status !== 'paid').length} of {booking.payment_shares.length} travelers still need to pay
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`mailto:${customer.email}`, '_blank')}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Email
                        </Button>
                        {customer.phone && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`tel:${customer.phone}`, '_blank')}
                          >
                            <Phone className="h-4 w-4 mr-1" />
                            Call
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
